/* eslint-disable react-hooks/rules-of-hooks */
import { observer } from "mobx-react";
import {
  Flex,
  Text,
  Tooltip,
} from "@mantine/core";
import { useStore } from "../../../../hooks/use-store";
import ImageObserved from "../Image";
import { PromptField } from "./PromptField";
import { PipelineParameters } from "../Pipeline";
import { priceMap } from "../Router";
import { useEffect, useRef, useState } from "react";
import CanvasDraw from "react-canvas-draw";
import { useRequest } from "ahooks";
import { NOTIFICATION_SERVICE } from "../../../../components/notifications/NotificationService";

const Element = (model: {
  buy: (params: PipelineParameters[]) => Promise<void>;
  loading: boolean
}) => {

  const {
    dataStore: { spaceStore, appStore },
  } = useStore();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedModel, setSelectedModel] = useState<'nanoBanana' | 'kontext'>('nanoBanana');
  const [inpaintMode, setInpaintMode] = useState(false);
  const [brushRadius, setBrushRadius] = useState(20);
  const [brushOpacity, setBrushOpacity] = useState(0.7);
  const renderRef = useRef<CanvasDraw>(null);
  const [canvasHeight, setCanvasHeight] = useState(1024);
  const [canvasWidth, setCanvasWidth] = useState(800);
  const [extraImages, setExtraImages] = useState<string[]>([]);
  const [selectedSize, setSelectedSize] = useState<'1K' | '2K' | '4K'>('4K');
  const sizeMultiplier = selectedSize === '1K' ? 1 : selectedSize === '2K' ? 2 : 4;
  const baseCredits = priceMap.AI_RETOUCH_2;
  const computedCredits = baseCredits * sizeMultiplier;

  const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  const composeWithBackground = async (minLongSide: number = 1200): Promise<string | null> => {
    if (!renderRef.current) return null;
    try {
      const strokesDataUrl = (renderRef.current as any).getDataURL('image/png', false);
      const bgSrc = spaceStore.selectedModel?.mainImage || spaceStore.selectedImage!;
      const [bgImg, strokesImg] = await Promise.all([
        loadImage(bgSrc),
        loadImage(strokesDataUrl),
      ]);

      const canvas = document.createElement('canvas');
      const currentRatio = canvasWidth / canvasHeight;
      const targetLongSide = Math.max(minLongSide, Math.max(canvasWidth, canvasHeight));
      if (currentRatio >= 1) {
        canvas.width = targetLongSide;
        canvas.height = Math.round(targetLongSide / currentRatio);
      } else {
        canvas.height = targetLongSide;
        canvas.width = Math.round(targetLongSide * currentRatio);
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
      ctx.drawImage(strokesImg, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } catch (_e) {
      return null;
    }
  };

  // const handleDownloadComposite = async () => {
  //   try {
  //     const composed = await composeWithBackground(1200);
  //     const dataUrl = composed || (renderRef.current as any)?.getDataURL('image/png', true);
  //     if (!dataUrl) return;
  //     const link = document.createElement('a');
  //     link.href = dataUrl;
  //     link.download = 'screenshot.png';
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   } catch (_e) {
  //     // noop
  //   }
  // };

  // Convert base64 to remote URL
  const { runAsync: runAsyncConvertBase64 } = useRequest(
    async (model: { url: string }) => appStore.convertImageBase64({ url: model.url }),
    {
      manual: true,
      onSuccess: (res) => {
        if ((res as any).error) {
          NOTIFICATION_SERVICE.errorNotification(
            "Sorry, service is unavailable due to high demand. Please try later"
          );
        }
        return (res as any).url;
      },
    }
  );

  // Adjust canvas to image ratio
  useEffect(() => {
    const src = spaceStore.selectedModel?.mainImage || spaceStore.selectedImage;
    if (!src) return;
    const img = new Image();
    img.onload = function () {
      const ratio = img.width / img.height;
      if (img.width > img.height) {
        setCanvasWidth(800);
        setCanvasHeight(800 / ratio);
      } else {
        setCanvasHeight(1024);
        setCanvasWidth(1024 * ratio);
      }
    };
    img.src = src;
  }, [spaceStore.selectedModel?.mainImage, spaceStore.selectedImage]);

  const handleAddExtraImages = async (files: FileList | null) => {
    if (!files) return;
    const remaining = 5 - extraImages.length;
    const toProcess = Array.from(files).slice(0, remaining);
    for (const file of toProcess) {
      const base64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      try {
        const { url } = await runAsyncConvertBase64({ url: base64 }) as unknown as { url: string };
        setExtraImages((prev) => [...prev, url]);
      } catch (e) {
        NOTIFICATION_SERVICE.errorNotification("Failed to add image. Try another file.");
      }
    }
  };

  return (
    <Flex direction={'column'} w="100%" pos='relative' gap={12}                         >

      <Flex
        style={{
          position: 'absolute',
          top: '0',
          left: '50%',
          transform: 'translate(420px, 0)',
        }}
      >

        <Flex direction={'column'} gap={12}>
          <Text fz={32} fw={800} color='#fff' lh={1.2} mb={2}>AI Retouch.
          </Text>
          <Text fz={16} color='#fff' lh={1.2} maw={300} mb={12}>
            Modify your images with prompts or add new elements directly into the scene.<Tooltip
              label={
                <Text fw={500}>
                  Adjust your generated images in a controlled and flexible way. Change visual conditions such as weather, lighting, or motion blur simply by using a text prompt.
                </Text>
              }
              multiline
              w={320}
              withArrow
              position="bottom"
              color='dark'
            >
              <span style={{ marginLeft: 4, cursor: 'pointer', display: 'inline-block' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="#4badf4" className="icon icon-tabler icons-tabler-filled icon-tabler-info-square-rounded"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm0 9h-1l-.117 .007a1 1 0 0 0 0 1.986l.117 .007v3l.007 .117a1 1 0 0 0 .876 .876l.117 .007h1l.117 -.007a1 1 0 0 0 .876 -.876l.007 -.117l-.007 -.117a1 1 0 0 0 -.764 -.857l-.112 -.02l-.117 -.006v-3l-.007 -.117a1 1 0 0 0 -.876 -.876l-.117 -.007zm.01 -3l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007z" /></svg>
              </span>
            </Tooltip>
          </Text>

          <Flex
            direction="column"
            gap={12}
            style={{
              width: '300px',
              position: 'relative',
              padding: '18px',
              backgroundColor: 'rgba(32, 32, 32, 1)',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          >
            <Flex gap={8}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 1.96973H8C4 1.96973 2 3.96973 2 7.96973V12.9697C2 17.9697 4 18.9697 8 18.9697H8.5C8.78 18.9697 9.14 19.1497 9.3 19.3697L10.8 21.3697C11.46 22.2497 12.54 22.2497 13.2 21.3697L14.7 19.3697C14.89 19.1197 15.19 18.9697 15.5 18.9697H16C20 18.9697 22 16.9697 22 12.9697V7.96973C22 3.96973 20 1.96973 16 1.96973ZM8.53 12.1697C8.82 12.4597 8.82 12.9397 8.53 13.2297C8.38 13.3797 8.19 13.4497 8 13.4497C7.81 13.4497 7.62 13.3797 7.47 13.2297L5.47 11.2297C5.18 10.9397 5.18 10.4597 5.47 10.1697L7.47 8.16973C7.76 7.87973 8.24 7.87973 8.53 8.16973C8.82 8.45973 8.82 8.93973 8.53 9.22973L7.06 10.6997L8.53 12.1697ZM13.69 8.65973L11.69 13.3297C11.57 13.6097 11.29 13.7797 11 13.7797C10.9 13.7797 10.8 13.7597 10.7 13.7197C10.32 13.5597 10.14 13.1197 10.31 12.7297L12.31 8.05973C12.47 7.67973 12.91 7.49973 13.3 7.66973C13.68 7.83973 13.85 8.27973 13.69 8.65973ZM18.53 11.2297L16.53 13.2297C16.38 13.3797 16.19 13.4497 16 13.4497C15.81 13.4497 15.62 13.3797 15.47 13.2297C15.18 12.9397 15.18 12.4597 15.47 12.1697L16.94 10.6997L15.47 9.22973C15.18 8.93973 15.18 8.45973 15.47 8.16973C15.76 7.87973 16.24 7.87973 16.53 8.16973L18.53 10.1697C18.82 10.4597 18.82 10.9397 18.53 11.2297Z" fill="#BDBDBD" />
              </svg>


              <Text color="white" size={'sm'} fw={700}>
                Prompt examples
              </Text>
            </Flex>
            <Text color="rgba(189, 189, 189, 1)" size={'sm'}>
              Click a prompt to add it
            </Text>
            {
              [
                'Convey a strong sense of speed: the vehicle is driving fast on the road. Keep the car sharp and in focus while adding natural motion blur to the background to emphasize movement and velocity.',
                'Transform the scene into a winter landscape at dawn, with snow-covered ground, cold tones, and soft early morning light on the horizon.',
                'Add orange painted lines and geometric shapes to the ground.',
                'Transform the scene into a rainy landscape with dark clouds overhead and dramatic lightning in the background. Emphasize wet surfaces, reflections, and a moody atmosphere.',
              ].map(prompt =>
                <Flex key={prompt} p={8} gap={8} wrap={'wrap'} style={{ cursor: 'pointer', border: '1px solid rgba(189, 189, 189, 0.2)', borderRadius: '4px', backgroundColor: 'rgba(32, 32, 32, 1)' }}
                  onClick={() => {
                    spaceStore.setPrompt(prompt);
                  }}>
                  <Text color="rgba(189, 189, 189, 1)" size={'sm'}>
                    {prompt}
                  </Text>
                </Flex>
              )
            }
          </Flex>
        </Flex>
      </Flex>
      {
        selectedModel === 'nanoBanana' && inpaintMode ? (
          <Flex w={'800px'} style={{ position: 'relative' }}>
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100%',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '6px',
                overflow: 'hidden'
              }}
            >
              <CanvasDraw
                ref={renderRef}
                backgroundColor='#00000028'
                lazyRadius={0}
                hideGrid={true}
                brushRadius={brushRadius}
                brushColor={`rgba(209,254,23,${brushOpacity})`}
                canvasHeight={canvasHeight}
                canvasWidth={canvasWidth}
                imgSrc={spaceStore.selectedModel?.mainImage || spaceStore.selectedImage!}
              />
            </div>
          </Flex>
        ) : (
          <ImageObserved
            src={spaceStore.selectedModel?.mainImage || spaceStore.selectedImage!}
            width="100%"
            height="auto"
            imgStyle={{ borderRadius: '6px', overflow: 'hidden' }}
          >
          </ImageObserved>
        )
      }
      <Flex
        style={{
          position: 'absolute',
          right: '-320px',
          top: '300px',
          width: '300px',
          zIndex: 1000,
        }}
        id="sidebar"
      >
      </Flex>
      <PromptField
        credits={selectedModel === 'nanoBanana' ? computedCredits : baseCredits}
        onClick={async () => {
          const imageSrc = spaceStore.selectedModel?.mainImage || spaceStore.selectedImage!;

          if (selectedModel === 'nanoBanana') {
            const srcset: string[] = [imageSrc, ...extraImages];
            if (inpaintMode && renderRef.current) {

              if (extraImages.length > 0) {
                try {
                  const composed = await composeWithBackground();
                  const dataUrl = composed || (renderRef.current as any).getDataURL('image/png', true);
                  const { url } = await runAsyncConvertBase64({ url: dataUrl }) as unknown as { url: string };
                  srcset.push(url);

                  const pipeline: PipelineParameters = {
                    provider: 'custom',
                    type: 'nanoBananaMultiple',
                    prompt: spaceStore.prompt,
                    hiddenPrompt:
                      `The first image shows the exact placement of the object. Integrate it seamlessly into the scene with photorealistic quality:
  - Remove the green brush from the image.
  - Do not change ratio of first backplate.
  - Match the exact lighting direction, intensity, and color temperature of the scene
  - Add realistic shadows that match the scene's lighting (cast shadows on surfaces, contact shadows at base)
  - Apply appropriate highlights and reflections based on the scene's light sources
  - Match the depth of field and focus of the background
  - Ensure perfect color harmony with the scene's ambient light and color grading
  - Blend edges naturally with soft transitions where the object meets the background
  - Preserve all surface textures and material properties realistically
  - Match the perspective and viewing angle of the scene
  Keep the object's position, size, and orientation exactly as shown in the guide.
  Addtitional prompt from user: ${spaceStore.prompt}`,

                    // "Using the provided images, place the elements onto there area drawed with green masked area. The added element should integrate into the image naturally. The ratio of background shoudn't change. " + spaceStore.prompt,
                    inputSrcs: [url, ...extraImages],
                    inputSrc: imageSrc,
                    credits: computedCredits,
                    size: selectedSize,
                    srcset,
                  };
                  await model.buy([pipeline]);
                } catch (e) {
                  // ignore overlay failure, proceed without it
                }
              } else {
                try {
                  const composed = await composeWithBackground();
                  const dataUrl = composed || (renderRef.current as any).getDataURL('image/png', true);
                  const { url } = await runAsyncConvertBase64({ url: dataUrl }) as unknown as { url: string };
                  srcset.push(url);

                  const pipeline: PipelineParameters = {
                    provider: 'custom',
                    type: 'nanoBananaMultiple',
                    prompt: spaceStore.prompt,
                    hiddenPrompt:
                      `Green masked area is the area that should be retouched. Prompt from user: ${spaceStore.prompt}`,
                    inputSrcs: [url, ...extraImages],
                    inputSrc: imageSrc,
                    credits: computedCredits,
                    size: selectedSize,
                    srcset,
                  };
                  await model.buy([pipeline]);
                } catch (e) {
                  // ignore overlay failure, proceed without it
                }
              }
            } else {
              if (srcset.length > 0) {
                const pipeline: PipelineParameters = {
                  provider: 'custom',
                  type: 'nanoBananaMultiple',
                  prompt: spaceStore.prompt,
                  hiddenPrompt: spaceStore.prompt,
                  inputSrcs: [imageSrc],
                  inputSrc: imageSrc,
                  credits: computedCredits,
                  size: selectedSize,
                  srcset,
                };
                await model.buy([pipeline]);
              } else {
                const pipeline: PipelineParameters = {
                  provider: 'custom',
                  type: 'nanoBanana',
                  prompt: spaceStore.prompt,
                  inputSrc: imageSrc,
                  credits: computedCredits,
                  size: selectedSize,
                  srcset,
                };
                await model.buy([pipeline]);
              }
            }

          } else {
            const pipeline: PipelineParameters = {
              provider: 'custom',
              type: 'placeCarK',
              prompt: spaceStore.prompt,
              inputSrc: imageSrc,
              backgroundSrc: imageSrc,
              placementSrc: imageSrc,
              credits: baseCredits,
              srcset: [],
            };
            await model.buy([pipeline]);
          }
        }}
        value={spaceStore.prompt}
        onChangeEnd={(value) => {
          spaceStore.setPrompt(value)
        }}
        loading={model.loading}
        placeholder="Describe an image and click generate"
      >
        <Text pl={18} fz={24} fw={700} color='#fff' lh={1.2} mb={2}>Upload images.<Tooltip
              label={
                <Text fw={500}>
                  Upload additional image elements and place them within the scene using the inpaint tool. Just mark the area with the brush and let the AI blend it naturally into your visual.
                </Text>
              }
              multiline
              w={320}
              withArrow
              position="bottom"
              color='dark'
            >
              <span style={{ marginLeft: 4, cursor: 'pointer', display: 'inline-block' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="#4badf4" className="icon icon-tabler icons-tabler-filled icon-tabler-info-square-rounded"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm0 9h-1l-.117 .007a1 1 0 0 0 0 1.986l.117 .007v3l.007 .117a1 1 0 0 0 .876 .876l.117 .007h1l.117 -.007a1 1 0 0 0 .876 -.876l.007 -.117l-.007 -.117a1 1 0 0 0 -.764 -.857l-.112 -.02l-.117 -.006v-3l-.007 -.117a1 1 0 0 0 -.876 -.876l-.117 -.007zm.01 -3l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007z" /></svg>
              </span>
            </Tooltip></Text>
        <Flex gap={2} p={10} wrap={'wrap'} pr={'100px'} w={'100%'}>
          {/* <Flex
            gap={8}
            align="center"
            style={{
              backgroundColor: 'rgba(32, 32, 32, 1)',
              borderRadius: '8px',
              padding: '8px',
              width: 'fit-content'
            }}
          >
            <Text color="white" size="sm" fw={500}>Model:</Text>
            <Flex gap={4}>
              <button
                onClick={() => setSelectedModel('nanoBanana')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: selectedModel === 'nanoBanana' ? '#e8590c' : 'rgba(64, 64, 64, 1)',
                  color: 'white',
                  fontSize: '12px',
                  // fontWeight: selectedModel === 'nanoBanana' ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Nano Banana
              </button>
              <button
                onClick={() => setSelectedModel('kontext')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: selectedModel === 'kontext' ? '#e8590c' : 'rgba(64, 64, 64, 1)',
                  color: 'white',
                  fontSize: '12px',
                  // fontWeight: selectedModel === 'kontext' ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Kontext
              </button>
            </Flex>
          </Flex> */}

          {selectedModel === 'nanoBanana' && (
            <Flex gap={4} align="center" style={{ backgroundColor: 'rgba(32, 32, 32, 1)', borderRadius: '8px', padding: '8px' }}>
              <Text color="white" size="sm" fw={500}>Size:</Text>
              {(['1K', '2K', '4K'] as const).map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: selectedSize === size ? '#e8590c' : 'rgba(64, 64, 64, 1)',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minWidth: '48px'
                  }}
                >
                  {size}
                </button>
              ))}
           
            </Flex>
          )}

          {
            selectedModel === 'nanoBanana' && (
              <Flex gap={16} align="center" style={{ backgroundColor: 'rgba(32, 32, 32, 1)', borderRadius: '8px', padding: '8px' }}>
                <Text color="white" size="sm" fw={500}>Inpaint Tool:</Text>
                <button
                  onClick={() => {
                    setInpaintMode(!inpaintMode)
                    spaceStore.setPrompt('')
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: inpaintMode ? '#e8590c' : 'rgba(64, 64, 64, 1)',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {inpaintMode ? 'On' : 'Off'}
                </button>
                {inpaintMode && (
                  <Flex gap={12} align="center">
                    <Flex direction="column" gap={4}>
                      <Text color="white" size="xs">Brush Size: {brushRadius}px</Text>
                      <input type="range" min={1} max={80} value={brushRadius} onChange={(e) => setBrushRadius(parseInt(e.currentTarget.value))} />
                    </Flex>
                    <Flex direction="column" gap={4}>
                      <Text color="white" size="xs">Opacity: {Math.round(brushOpacity * 100)}%</Text>
                      <input type="range" min={0} max={100} value={Math.round(brushOpacity * 100)} onChange={(e) => setBrushOpacity(parseInt(e.currentTarget.value) / 100)} />
                    </Flex>
                    <button
                      onClick={() => renderRef.current?.clear()}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        backgroundColor: 'transparent',
                        color: 'white',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>

                  </Flex>
                )}
              </Flex>
            )
          }

          {
            selectedModel === 'nanoBanana' && (
              <Flex gap={8} direction="column" style={{ backgroundColor: 'rgba(32, 32, 32, 1)', borderRadius: '8px', padding: '8px', width: '100%' }}>
                <Text color="white" size="sm" fw={500}>Additional images (up to 5)</Text>
                <Flex gap={8} wrap={'wrap'}>
                  {extraImages.map((src, idx) => (
                    <Flex key={src + idx} direction={'column'} gap={4} align={'center'} style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '6px', width: '120px', height: '140px', }}>
                      <ImageObserved width={'80px'} height={'80px'} dark src={src} objectFit="contain" />
                      <button
                        onClick={() => setExtraImages(extraImages.filter((_, i) => i !== idx))}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: 'rgba(64, 64, 64, 1)',
                          color: 'white',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </Flex>
                  ))}
                  {extraImages.length < 5 && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '120px', height: '140px', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '6px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)' }}>
                      + Add
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => handleAddExtraImages(e.currentTarget.files)}
                      />
                    </label>
                  )}
                </Flex>
              </Flex>
            )
          }
        </Flex>
      </PromptField>
    </Flex >
  );
};

export const AIRetouch2 = observer(Element);